import type { IMessageType, JsonValue } from "@protobuf-ts/runtime";
import { API_BASE } from "@/config/build";
import { ValidationFailure, VersionConflict } from "@/generated/backend/v1/family";
import type { FieldError } from "@/generated/core/v1/value_source";
import type { FamilyDocument } from "@/generated/backend/v1/family";

/**
 * The one way the app talks to the backend: proto3 JSON over HTTP with a Firebase Bearer token.
 * Requests are serialized with `toJson`; responses are parsed with `fromJson` and
 * `ignoreUnknownFields`, so stored plans with old fields still parse.
 */

/** Error body: `{ "error": { "code", "message" } }`, or a proto body for 409/422. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** 409: the newer family document the save conflicted with. */
  readonly conflict?: FamilyDocument;
  /** 422: the engine's validation errors, by field path. */
  readonly fieldErrors: FieldError[];
  /** The parsed error body, for endpoints that put more in it (GET /plan's `pendingJob`). */
  body?: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    extra: { conflict?: FamilyDocument; fieldErrors?: FieldError[] } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.conflict = extra.conflict;
    this.fieldErrors = extra.fieldErrors ?? [];
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
  get isValidation(): boolean {
    return this.status === 422;
  }
}

/** Something that returns the current ID token, or null when signed out. */
export type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider = async () => null;

/** Installed by the auth layer once Firebase is ready. */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type FetchOptions<Req extends object, Res extends object> = {
  method?: Method;
  /** Proto message to send, serialized with `request.toJson`. */
  body?: Req;
  request?: IMessageType<Req>;
  /** Plain JSON body for the endpoints that aren't proto (Stripe, admin, blog). */
  json?: unknown;
  /** Proto type to parse the response with. Omit for plain JSON or 204. */
  response?: IMessageType<Res>;
  /** Skip the Authorization header (public endpoints such as /config). */
  anonymous?: boolean;
  signal?: AbortSignal;
};

export const READ_OPTIONS = { ignoreUnknownFields: true } as const;

function url(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  const base = API_BASE.replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

async function readError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // not JSON
  }
  const error = errorFrom(res, body);
  error.body = body;
  if (error.code === "EMAIL_NOT_VERIFIED") onUnverified();
  return error;
}

let onUnverified: () => void = () => undefined;

/** Installed by the app shell: a 403 EMAIL_NOT_VERIFIED sends the user to the verify-email gate. */
export function setUnverifiedHandler(handler: () => void): void {
  onUnverified = handler;
}

function errorFrom(res: Response, body: unknown): ApiError {
  const fallback = `Request failed (${res.status} ${res.statusText})`;
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    if (res.status === 409 && "current" in record) {
      const conflict = VersionConflict.fromJson(record as JsonValue, READ_OPTIONS);
      return new ApiError(409, "VERSION_CONFLICT", "Your household changed somewhere else.", {
        conflict: conflict.current,
      });
    }
    if (res.status === 422 && "errors" in record) {
      const failure = ValidationFailure.fromJson(record as JsonValue, READ_OPTIONS);
      return new ApiError(
        422,
        "VALIDATION_FAILED",
        failure.message || "Check the highlighted answers.",
        {
          fieldErrors: failure.errors,
        },
      );
    }
    const error = record.error;
    if (error && typeof error === "object") {
      const e = error as Record<string, unknown>;
      return new ApiError(
        res.status,
        typeof e.code === "string" ? e.code : String(res.status),
        typeof e.message === "string" ? e.message : fallback,
      );
    }
    if (typeof error === "string") return new ApiError(res.status, String(res.status), error);
    if (typeof record.message === "string") {
      return new ApiError(res.status, String(res.status), record.message);
    }
  }
  return new ApiError(res.status, String(res.status), fallback);
}

export async function fetchApi<Res extends object = object, Req extends object = object>(
  path: string,
  options: FetchOptions<Req, Res> = {},
): Promise<Res> {
  const { body, request, json, response, anonymous, signal } = options;
  const method: Method =
    options.method ?? (body !== undefined || json !== undefined ? "POST" : "GET");

  const headers: Record<string, string> = { Accept: "application/json" };
  let payload: string | undefined;
  if (body !== undefined) {
    if (!request) throw new Error("fetchApi: a proto body needs its request type");
    payload = JSON.stringify(request.toJson(body));
  } else if (json !== undefined) {
    payload = JSON.stringify(json);
  }
  if (payload !== undefined) headers["Content-Type"] = "application/json";
  if (!anonymous) {
    const token = await tokenProvider();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(url(path), { method, headers, body: payload, signal });
  if (!res.ok) throw await readError(res);
  if (res.status === 204 || !response) {
    if (res.status === 204) return undefined as unknown as Res;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as Res;
  }
  const data = (await res.json()) as JsonValue;
  return response.fromJson(data, READ_OPTIONS);
}

/** The same as fetchApi for endpoints that answer 404 when the thing doesn't exist yet. */
export async function fetchOrNull<Res extends object>(
  path: string,
  options: FetchOptions<object, Res> = {},
): Promise<Res | null> {
  try {
    return await fetchApi<Res>(path, options);
  } catch (error) {
    if (error instanceof ApiError && error.isNotFound) return null;
    throw error;
  }
}
